<template>
  <div id="week-resources">
    <v-container fluid class="flex-grow-0 pa-0">
      <v-row class="ma-0 header">
        <v-col v-for="(day, date) in tasksByDay" :key="date">
          <v-row class="align-baseline">
            <v-col cols="12" class="d-flex justify-space-between p-2" :class="$utils.datePPF(date)">
              <span class="no-border text-body-2">{{ $moment(date).format('ddd') }}</span>
              <v-chip
                class="text-body-2"
                variant="flat"
                :color="$utils.datePPF(date) === 'present' ? 'light-blue-lighten-1' : 'light-blue-lighten-3'"
                >Total Mu: {{ muTotalOfDay(date) }}</v-chip
              >
            </v-col>
            <v-col cols="2" class="py-1 no-border text-h4" :class="$utils.datePPF(date)">
              {{ $moment(date).format('D') }}
            </v-col>
            <v-col cols="10" class="py-1 text-right d-flex justify-end align-baseline">
              <div class="mr-2">
                <v-icon size="small" class="cutting mb-1">mdi-hand-saw</v-icon>
                <input
                  :style="{ width: '30px' }"
                  class="outlined text-center"
                  name="cutting"
                  size="mini"
                  v-model.number="capacityObject[date].cutting"
                  @input="$emit('cutHoursChanged', capacityObject[date].date.seconds, $event.target.value)"
                  type="number"
                />
              </div>
              <v-divider vertical></v-divider>
              <div class="mx-3">
                <div
                  class="text-body-1"
                  :class="{
                    'text-red': getWorkshopMuCapacityPercentage(date) < 100,
                    'text-green': getWorkshopMuCapacityPercentage(date) >= 100
                  }"
                >
                  {{ getWorkshopMuCapacityPercentage(date) }}%
                </div>
              </div>
              <div class="text-caption text-medium-emphasis">of {{ getWorkshopMuRequirement(date) }}Mμ</div>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <v-container fluid class="flex-grow-1 pa-0 d-flex flex-column">
      <div class="worker-type working text-body-2 pl-2 py-1">Working</div>
      <v-row class="ma-0">
        <v-col v-for="(day, dateKey) in capacityObject" class="ma-0 pa-0" :key="dateKey">
          <draggable
            class="draggable"
            :class="{
              disabled:
                day.working.find((o) => {
                  return o.timetasticId === draggedEmployee
                }) != undefined && day.date.seconds != activeDaySeconds
            }"
            :disabled="resetPreviewMode || holidayPreviewMode"
            v-model="day.working"
            :group="
              day.working.find((o) => {
                return o.timetasticId === draggedEmployee
              }) == undefined || day.date.seconds === activeDaySeconds
                ? 'employed'
                : null
            "
            @start="
              (e) => {
                draggedEmployee = day.working[e.oldIndex].timetasticId
                activeDaySeconds = day.date.seconds
              }
            "
            @end="
              () => {
                draggedEmployee = null
                activeDaySeconds = null
              }
            "
            @add="$emit('resourcesChanged', day.date.seconds)"
            @remove="$emit('resourcesChanged', day.date.seconds)"
            item-key="timetasticId"
          >
            <template #item="{ element: working }">
              <v-hover class="worker working" v-slot="{ isHovering, props }">
                <div
                  class="worker working"
                  :class="{
                    'preview-remove':
                      (resetPreviewMode && isWorkingMarkedForRemoval(dateKey, working)) ||
                      (holidayPreviewMode && isWorkingMarkedForHolidayRemoval(dateKey, working))
                  }"
                  v-bind="props"
                  @click="
                    () => {
                      selectedAdjustment = working
                      activeDaySeconds = day.date.seconds
                      adjustmentDialog = true
                    }
                  "
                >
                  <div class="name">{{ working.name }}</div>
                  <v-icon v-if="isHovering">mdi-pencil</v-icon>
                  <div class="hours" v-else>{{ working.hours }}</div>
                </div>
              </v-hover>
            </template>
          </draggable>
          <template v-if="resetPreviewMode && previewDiffs[dateKey]">
            <div
              v-for="addition in previewDiffs[dateKey].additions"
              :key="'add-' + addition.timetasticId"
              class="worker working preview-add"
            >
              <div class="name">{{ addition.name }}</div>
              <div class="hours">{{ addition.hours }}</div>
            </div>
          </template>
          <template v-if="holidayPreviewMode && holidayPreviewDiffs[dateKey]">
            <div
              v-for="restoration in holidayPreviewDiffs[dateKey].workingRestorations"
              :key="'restore-' + restoration.timetasticId"
              class="worker working preview-add"
            >
              <div class="name">{{ restoration.name }}</div>
              <div class="hours">{{ restoration.hours }}</div>
            </div>
          </template>
        </v-col>
      </v-row>

      <div class="worker-type working text-body-2 pl-2 py-1">Freelancers</div>
      <v-row class="ma-0">
        <v-col v-for="(day, key) in capacityObject" class="ma-0 pa-0" :key="key">
          <v-hover
            class="worker working"
            v-for="(worker, key) of day.freelancers"
            :key="key"
            v-slot="{ isHovering, props }"
          >
            <div class="worker working" v-bind="props" @click="removeFreelancer(day, worker)">
              <div class="name">{{ worker.name }}</div>
              <v-icon v-if="isHovering">mdi-delete</v-icon>
              <div v-else class="hours">{{ worker.hours }}</div>
            </div>
          </v-hover>

          <div v-if="!resetPreviewMode && !holidayPreviewMode">
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" size="small" variant="text" class="add-resource-btn">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item v-for="staff in availableFreelancers" :key="staff.id" class="d-flex align-center">
                  <div class="text-body-2">{{ staff.name }}</div>
                  <v-spacer></v-spacer>
                  <div class="hours-input-wrapper mx-1">
                    <input
                      type="number"
                      min="0"
                      max="8"
                      step="1"
                      class="hours-input text-body-2"
                      :value="getFreelancerHours(staff.id)"
                      @input="setFreelancerHours(staff.id, $event.target.value)"
                      @click.stop
                    /><span class="hours-suffix text-body-2">hr</span>
                  </div>
                  <v-btn
                    icon
                    variant="flat"
                    density="compact"
                    size="x-small"
                    color="success"
                    @click="addFreelancer(day, staff, getFreelancerHours(staff.id))"
                  >
                    <v-icon size="small">mdi-check</v-icon>
                  </v-btn>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </v-col>
      </v-row>

      <div class="worker-type overtime text-body-2 pl-2 py-1">Overtime</div>
      <v-row class="ma-0">
        <v-col v-for="(day, key) in capacityObject" class="ma-0 pa-0" :key="key">
          <v-hover
            class="worker working"
            v-for="(worker, key) of day.overtime"
            :key="key"
            v-slot="{ isHovering, props }"
          >
            <div class="worker working" v-bind="props" @click="removeOvertime(day, worker)">
              <div class="name">{{ worker.name }}</div>
              <v-icon v-if="isHovering">mdi-delete</v-icon>
              <div v-else class="hours">{{ worker.hours }}</div>
            </div>
          </v-hover>

          <div v-if="!resetPreviewMode && !holidayPreviewMode">
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" size="small" variant="text" class="add-resource-btn">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>

              <v-list>
                <v-list-item>
                  <div class="mini-title">Working</div>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item v-for="staff in day.working" :key="staff.timetasticId" class="d-flex align-center">
                  <div class="text-body-2">{{ staff.name }}</div>
                  <v-spacer></v-spacer>
                  <div class="hours-input-wrapper mx-1">
                    <input
                      type="number"
                      min="0"
                      max="8"
                      step="1"
                      class="hours-input text-body-2"
                      :value="getOvertimeHours(staff.timetasticId)"
                      @input="setOvertimeHours(staff.timetasticId, $event.target.value)"
                      @click.stop
                    /><span class="hours-suffix text-body-2">hr</span>
                  </div>
                  <v-btn
                    icon
                    variant="flat"
                    density="compact"
                    size="x-small"
                    color="success"
                    @click="addOvertime(day, staff, getOvertimeHours(staff.timetasticId))"
                  >
                    <v-icon size="small">mdi-check</v-icon>
                  </v-btn>
                </v-list-item>

                <div>
                  <v-list-item>
                    <div class="mini-title">Not Working</div>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item
                    v-for="staff in $store.getters.notWorking(day)"
                    :key="staff.timetasticId"
                    class="d-flex align-center"
                  >
                    <div class="text-body-2">{{ staff.name }}</div>
                    <v-spacer></v-spacer>
                    <div class="hours-input-wrapper mx-1">
                      <input
                        type="number"
                        min="0"
                        max="8"
                        step="1"
                        class="hours-input text-body-2"
                        :value="getOvertimeHours(staff.timetasticId)"
                        @input="setOvertimeHours(staff.timetasticId, $event.target.value)"
                        @click.stop
                      /><span class="hours-suffix text-body-2">hr</span>
                    </div>
                    <v-btn
                      icon
                      variant="flat"
                      density="compact"
                      size="x-small"
                      color="success"
                      @click="addOvertime(day, staff, getOvertimeHours(staff.timetasticId))"
                    >
                      <v-icon size="small">mdi-check</v-icon>
                    </v-btn>
                  </v-list-item>
                </div>
              </v-list>
            </v-menu>
          </div>
        </v-col>
      </v-row>

      <div class="worker-type holidays text-body-2 pl-2 py-1">Holidays</div>
      <v-row class="ma-0">
        <v-col v-for="(day, dateKey) in capacityObject" class="ma-0 pa-0" :key="dateKey">
          <v-hover
            class="worker holiday"
            v-for="(worker, idx) of day.holidays"
            :key="idx"
            v-slot="{ isHovering, props }"
          >
            <div
              class="worker holiday"
              :class="{
                'preview-remove':
                  (resetPreviewMode && isHolidayMarkedForRemoval(dateKey, worker)) ||
                  (holidayPreviewMode && isHolidayMarkedForHolidayRemoval(dateKey, worker))
              }"
              v-bind="props"
              @click="removeHoliday(day, worker)"
            >
              <div class="name">{{ worker.name }}</div>
              <v-icon v-if="isHovering">mdi-delete</v-icon>
              <div class="hours" v-else>{{ getHolidayHours(dateKey, worker) }}</div>
            </div>
          </v-hover>
          <template v-if="holidayPreviewMode && holidayPreviewDiffs[dateKey]">
            <div
              v-for="addition in holidayPreviewDiffs[dateKey].holidayAdditions"
              :key="'hol-add-' + addition.timetasticId"
              class="worker holiday preview-add"
            >
              <div class="name">{{ addition.name }}</div>
              <div class="hours">{{ getHolidayHours(dateKey, addition) }}</div>
            </div>
          </template>
          <div v-if="!resetPreviewMode && !holidayPreviewMode">
            <v-menu
              @update:model-value="
                (val) => {
                  if (!val) cancelHolidaySelection()
                }
              "
            >
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" size="small" variant="text" class="add-resource-btn">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <v-list>
                <template v-if="!holidaySelectionStaff">
                  <v-list-item>
                    <div class="mini-title">Working</div>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item
                    v-for="staff in day.working.filter(
                      (w) =>
                        !day.sick.some((s) => s.timetasticId === w.timetasticId) &&
                        !day.holidays.some((h) => h.timetasticId === w.timetasticId)
                    )"
                    :key="'w-' + staff.timetasticId"
                    class="d-flex"
                    @click.stop="selectStaffForHoliday(day, staff)"
                  >
                    <div class="text-body-2">{{ staff.name }}</div>
                  </v-list-item>
                  <v-list-item>
                    <div class="mini-title">Not Working</div>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item
                    v-for="staff in notWorking(day).filter(
                      (w) =>
                        !day.sick.some((s) => s.timetasticId === w.timetasticId) &&
                        !day.holidays.some((h) => h.timetasticId === w.timetasticId)
                    )"
                    :key="'nw-' + staff.timetasticId"
                    class="d-flex"
                    @click.stop="selectStaffForHoliday(day, staff)"
                  >
                    <div class="text-body-2">{{ staff.name }}</div>
                  </v-list-item>
                </template>
                <template v-else>
                  <v-list-item>
                    <div class="text-body-2 font-weight-bold">{{ holidaySelectionStaff.name }}</div>
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item class="d-flex justify-center">
                    <v-chip
                      color="amber"
                      class="mr-2"
                      @click="addHoliday(holidaySelectionDay, holidaySelectionStaff, 'half')"
                      >1/2 Day</v-chip
                    >
                    <v-chip color="accent" @click="addHoliday(holidaySelectionDay, holidaySelectionStaff, 'full')"
                      >Full Day</v-chip
                    >
                  </v-list-item>
                  <v-divider></v-divider>
                  <v-list-item @click.stop="cancelHolidaySelection">
                    <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
                    <div class="text-body-2">Back</div>
                  </v-list-item>
                </template>
              </v-list>
            </v-menu>
          </div>
        </v-col>
      </v-row>

      <div class="worker-type sick text-body-2 pl-2 py-1">Sick</div>
      <v-row class="ma-0">
        <v-col v-for="(day, dateKey) in capacityObject" class="ma-0 pa-0" :key="dateKey">
          <v-hover class="worker sick" v-for="(worker, idx) of day.sick" :key="idx" v-slot="{ isHovering, props }">
            <div
              class="worker sick"
              :class="{ 'preview-remove': resetPreviewMode && isSickMarkedForRemoval(dateKey, worker) }"
              v-bind="props"
              @click="removeSick(day, worker)"
            >
              <div class="name">{{ worker.name }}</div>
              <v-icon v-if="isHovering">mdi-delete</v-icon>
            </div>
          </v-hover>
          <div v-if="!resetPreviewMode && !holidayPreviewMode">
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn v-bind="props" size="small" variant="text" class="add-resource-btn">
                  <v-icon>mdi-plus</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item>
                  <div class="mini-title">Working</div>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item
                  v-for="staff in day.working.filter((w) => !day.holidays.some((h) => h.name === w.name))"
                  :key="'w-' + staff.timetasticId"
                  class="d-flex"
                  @click="addSick(day, staff)"
                >
                  <div class="text-body-2">{{ staff.name }}</div>
                </v-list-item>
                <v-list-item>
                  <div class="mini-title">Not Working</div>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item
                  v-for="staff in notWorking(day).filter((w) => !day.holidays.some((h) => h.name === w.name))"
                  :key="'nw-' + staff.timetasticId"
                  class="d-flex"
                  @click="addSick(day, staff)"
                >
                  <div class="text-body-2">{{ staff.name }}</div>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <v-dialog v-model="adjustmentDialog" width="400">
      <AdjustmentDetail
        v-model:data="selectedAdjustment"
        @change="$emit('resourcesChanged', activeDaySeconds)"
      ></AdjustmentDetail>
    </v-dialog>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import AdjustmentDetail from '@/components/schedule/AdjustmentDetail'

export default {
  name: 'WeekResources',
  components: { draggable, AdjustmentDetail },
  props: [
    'taskTypeGroups',
    'tasksByDay',
    'focussedTask',
    'capacityObject',
    'resetPreviewMode',
    'holidayPreviewMode',
    'timetasticHolidays'
  ],
  data() {
    return {
      adjustmentDialog: false,
      selectedAdjustment: 'hello',
      draggedEmployee: null,
      activeDaySeconds: null,
      overtimeHoursMap: {},
      freelancerHoursMap: {},
      holidaySelectionStaff: null,
      holidaySelectionDay: null
    }
  },
  computed: {
    availableFreelancers() {
      return (this.$store.state.staff || []).filter((staff) => {
        return staff.staffType === 'freelance' && !staff.departureDate
      })
    },
    availableEmployed() {
      return (this.$store.state.staff || []).filter((staff) => {
        return staff.staffType === 'employed' && !staff.departureDate
      })
    },
    previewDiffs() {
      if (!this.resetPreviewMode || !this.capacityObject) return {}
      const diffs = {}
      for (const [dateKey, day] of Object.entries(this.capacityObject)) {
        const additions = []
        const workingRemovals = new Set()
        const momentDay = this.$moment(dateKey)
        // Check for staff that should be added to working
        for (const staff of this.availableEmployed) {
          const schedule = this.$store.getters.staffHoursByMoment(momentDay, staff)
          if (!schedule) continue
          const hours = schedule[momentDay.isoWeekday() - 1]
          if (!hours || hours <= 0) continue
          const isWorking = day.working.some((w) => w.timetasticId === staff.timetasticId)
          if (isWorking) continue
          additions.push({ timetasticId: staff.timetasticId, name: staff.name, hours })
        }
        // Check for working staff that have no capacity for this day
        for (const worker of day.working) {
          const staff = this.availableEmployed.find((s) => s.timetasticId === worker.timetasticId)
          if (!staff) {
            workingRemovals.add(worker.timetasticId)
            continue
          }
          const schedule = this.$store.getters.staffHoursByMoment(momentDay, staff)
          if (!schedule) {
            workingRemovals.add(worker.timetasticId)
            continue
          }
          const hours = schedule[momentDay.isoWeekday() - 1]
          if (!hours || hours <= 0) {
            workingRemovals.add(worker.timetasticId)
          }
        }
        if (additions.length > 0 || workingRemovals.size > 0) {
          diffs[dateKey] = { additions, workingRemovals }
        }
      }
      return diffs
    },
    holidayPreviewDiffs() {
      if (!this.holidayPreviewMode || !this.timetasticHolidays || !this.capacityObject) return {}
      const diffs = {}

      // Build a set of timetastic holidays per dateKey
      const timetasticByDate = {}
      for (const holiday of this.timetasticHolidays) {
        if (holiday.status !== 'Approved') continue
        const isHalfDay =
          holiday.duration <= 0.5 ||
          (holiday.startType === holiday.endType &&
            (holiday.startType === 'Morning' || holiday.startType === 'Afternoon'))
        for (let i = 0; i < holiday.duration; i++) {
          const momentDay = this.$moment(holiday.startDate).add(i, 'days')
          const dateKey = momentDay.format('YYYY-MM-DD')
          if (!timetasticByDate[dateKey]) timetasticByDate[dateKey] = []
          const worker = this.$store.state.staff.find(
            (s) => s.staffType === 'employed' && s.timetasticId === holiday.userId
          )
          if (worker) {
            timetasticByDate[dateKey].push({
              timetasticId: worker.timetasticId,
              name: worker.name,
              type: isHalfDay ? 'half' : 'full',
              momentDay
            })
          }
        }
      }

      for (const [dateKey, day] of Object.entries(this.capacityObject)) {
        const holidayAdditions = []
        const holidayRemovals = new Set()
        const workingRemovals = new Set()
        const workingRestorations = []
        const workingHourChanges = new Map()

        const ttHolidays = timetasticByDate[dateKey] || []

        // Additions: holidays in Timetastic but not in current capacity holidays
        for (const ttHol of ttHolidays) {
          const alreadyOnHoliday = day.holidays.find((h) => h.timetasticId === ttHol.timetasticId)
          if (!alreadyOnHoliday) {
            holidayAdditions.push({ name: ttHol.name, timetasticId: ttHol.timetasticId, type: ttHol.type })
            // Full day means they'll be removed from working
            if (ttHol.type === 'full') {
              const isWorking = day.working.some((w) => w.timetasticId === ttHol.timetasticId)
              if (isWorking) workingRemovals.add(ttHol.timetasticId)
            } else {
              // Half day: show adjusted hours
              const staffMember = this.$store.state.staff.find((s) => s.timetasticId === ttHol.timetasticId)
              if (staffMember && ttHol.momentDay) {
                const schedule = this.$store.getters.staffHoursByMoment(ttHol.momentDay, staffMember)
                if (schedule) {
                  const fullHours = schedule[ttHol.momentDay.isoWeekday() - 1]
                  if (fullHours > 0) {
                    workingHourChanges.set(ttHol.timetasticId, Math.round((fullHours / 2) * 10) / 10)
                  }
                }
              }
            }
          } else if (alreadyOnHoliday.type !== ttHol.type) {
            // Type changed (e.g. full→half or half→full) — show as removal + addition
            holidayRemovals.add(alreadyOnHoliday.timetasticId)
            holidayAdditions.push({ name: ttHol.name, timetasticId: ttHol.timetasticId, type: ttHol.type })
            // half→full: worker still in working (with halved hours) needs to be removed
            if (ttHol.type === 'full') {
              const isWorking = day.working.some((w) => w.timetasticId === ttHol.timetasticId)
              if (isWorking) workingRemovals.add(ttHol.timetasticId)
            }
          }
        }

        // Removals: holidays in current capacity but NOT in Timetastic (stale)
        const ttIds = new Set(ttHolidays.map((h) => h.timetasticId))
        for (const existing of day.holidays) {
          if (!ttIds.has(existing.timetasticId)) {
            holidayRemovals.add(existing.timetasticId)
            // This person should be restored to working
            const staffMember = this.$store.state.staff.find((s) => s.timetasticId === existing.timetasticId)
            if (staffMember) {
              const momentDay = this.$moment(dateKey)
              const schedule = this.$store.getters.staffHoursByMoment(momentDay, staffMember)
              if (schedule) {
                const hours = schedule[momentDay.isoWeekday() - 1]
                if (hours > 0) {
                  const alreadyWorking = day.working.some((w) => w.timetasticId === existing.timetasticId)
                  if (!alreadyWorking) {
                    workingRestorations.push({ name: staffMember.name, timetasticId: staffMember.timetasticId, hours })
                  }
                }
              }
            }
          }
        }

        if (
          holidayAdditions.length > 0 ||
          holidayRemovals.size > 0 ||
          workingRemovals.size > 0 ||
          workingRestorations.length > 0 ||
          workingHourChanges.size > 0
        ) {
          diffs[dateKey] = {
            holidayAdditions,
            holidayRemovals,
            workingRemovals,
            workingRestorations,
            workingHourChanges
          }
        }
      }

      return diffs
    }
  },
  methods: {
    isWorkingMarkedForRemoval(dateKey, worker) {
      const diff = this.previewDiffs[dateKey]
      return diff && diff.workingRemovals.has(worker.timetasticId)
    },
    isSickMarkedForRemoval(dateKey, worker) {
      const diff = this.previewDiffs[dateKey]
      return diff && diff.additions.some((a) => a.timetasticId === worker.timetasticId)
    },
    isHolidayMarkedForRemoval(dateKey, worker) {
      const diff = this.previewDiffs[dateKey]
      return diff && diff.additions.some((a) => a.timetasticId === worker.timetasticId || a.name === worker.name)
    },
    isWorkingMarkedForHolidayRemoval(dateKey, worker) {
      const diff = this.holidayPreviewDiffs[dateKey]
      return diff && diff.workingRemovals.has(worker.timetasticId)
    },
    isHolidayMarkedForHolidayRemoval(dateKey, worker) {
      const diff = this.holidayPreviewDiffs[dateKey]
      return diff && diff.holidayRemovals.has(worker.timetasticId)
    },
    applyReset() {
      for (const [dateKey, day] of Object.entries(this.capacityObject)) {
        const momentDay = this.$moment(dateKey)
        let changed = false
        // Remove working staff with no capacity (splice in-place to preserve reactivity)
        for (let i = day.working.length - 1; i >= 0; i--) {
          const w = day.working[i]
          const staff = this.availableEmployed.find((s) => s.timetasticId === w.timetasticId)
          let shouldRemove = false
          if (!staff) {
            shouldRemove = true
          } else {
            const schedule = this.$store.getters.staffHoursByMoment(momentDay, staff)
            if (!schedule) {
              shouldRemove = true
            } else {
              const hours = schedule[momentDay.isoWeekday() - 1]
              if (!hours || hours <= 0) shouldRemove = true
            }
          }
          if (shouldRemove) {
            day.working.splice(i, 1)
            changed = true
          }
        }
        // Add staff that should be working
        for (const staff of this.availableEmployed) {
          const schedule = this.$store.getters.staffHoursByMoment(momentDay, staff)
          if (!schedule) continue
          const hours = schedule[momentDay.isoWeekday() - 1]
          if (!hours || hours <= 0) continue
          if (day.working.some((w) => w.timetasticId === staff.timetasticId)) continue
          day.working.push({ timetasticId: staff.timetasticId, name: staff.name, hours })
          // Remove from sick and holidays (splice in-place to preserve reactivity)
          for (let i = day.sick.length - 1; i >= 0; i--) {
            if (day.sick[i].timetasticId === staff.timetasticId) day.sick.splice(i, 1)
          }
          for (let i = day.holidays.length - 1; i >= 0; i--) {
            const h = day.holidays[i]
            if (h.timetasticId === staff.timetasticId || h.name === staff.name) day.holidays.splice(i, 1)
          }
          changed = true
        }
        if (changed) this.$emit('resourcesChanged', day.date.seconds)
      }
      this.$emit('resetApplied')
    },
    applyHolidayUpdate() {
      if (!this.timetasticHolidays || !this.capacityObject) return

      for (const [dateKey, diff] of Object.entries(this.holidayPreviewDiffs)) {
        const day = this.capacityObject[dateKey]
        if (!day) continue
        let changed = false

        // Add new holidays
        for (const addition of diff.holidayAdditions) {
          const alreadyOnHoliday = day.holidays.find((h) => h.timetasticId === addition.timetasticId)
          if (alreadyOnHoliday) {
            // Type changed — update it
            if (alreadyOnHoliday.type !== addition.type) {
              alreadyOnHoliday.type = addition.type
              changed = true
            }
          } else {
            day.holidays.push({
              id: addition.timetasticId + 'HOL',
              name: addition.name,
              timetasticId: addition.timetasticId,
              type: addition.type
            })
            changed = true
          }

          if (addition.type === 'full') {
            // Remove from working
            const idx = day.working.findIndex((w) => w.timetasticId === addition.timetasticId)
            if (idx !== -1) {
              day.working.splice(idx, 1)
              changed = true
            }
          } else {
            // Half day: halve working hours
            const workingEntry = day.working.find((w) => w.timetasticId === addition.timetasticId)
            if (workingEntry) {
              const staffMember = this.$store.state.staff.find((s) => s.timetasticId === addition.timetasticId)
              if (staffMember) {
                const momentDay = this.$moment(dateKey)
                const schedule = this.$store.getters.staffHoursByMoment(momentDay, staffMember)
                if (schedule) {
                  const fullHours = schedule[momentDay.isoWeekday() - 1]
                  workingEntry.hours = Math.round((fullHours / 2) * 10) / 10
                  changed = true
                }
              }
            }
          }
        }

        // Remove stale holidays and restore workers
        const additionIds = new Set(diff.holidayAdditions.map((a) => a.timetasticId))
        for (const timetasticId of diff.holidayRemovals) {
          // Skip if this is a type change (already handled in additions loop)
          if (additionIds.has(timetasticId)) continue

          const holIdx = day.holidays.findIndex((h) => h.timetasticId === timetasticId)
          if (holIdx !== -1) {
            day.holidays.splice(holIdx, 1)
            changed = true
          }

          // Restore to working if not already there
          const alreadyWorking = day.working.some((w) => w.timetasticId === timetasticId)
          if (!alreadyWorking) {
            const staffMember = this.$store.state.staff.find((s) => s.timetasticId === timetasticId)
            if (staffMember) {
              const momentDay = this.$moment(dateKey)
              const schedule = this.$store.getters.staffHoursByMoment(momentDay, staffMember)
              if (schedule) {
                const hours = schedule[momentDay.isoWeekday() - 1]
                if (hours > 0) {
                  day.working.push({ timetasticId: staffMember.timetasticId, name: staffMember.name, hours })
                  changed = true
                }
              }
            }
          } else {
            // Was half-day removal: restore full hours
            const workingEntry = day.working.find((w) => w.timetasticId === timetasticId)
            if (workingEntry) {
              const staffMember = this.$store.state.staff.find((s) => s.timetasticId === timetasticId)
              if (staffMember) {
                const momentDay = this.$moment(dateKey)
                const schedule = this.$store.getters.staffHoursByMoment(momentDay, staffMember)
                if (schedule) {
                  workingEntry.hours = schedule[momentDay.isoWeekday() - 1]
                  changed = true
                }
              }
            }
          }
        }

        if (changed) this.$emit('resourcesChanged', day.date.seconds)
      }

      this.$emit('holidayPreviewApplied')
    },
    addHoliday(day, employed, type = 'full') {
      day.holidays.push({
        id: (employed.timetasticId || employed.id) + 'HOL',
        name: employed.name,
        timetasticId: employed.timetasticId,
        type
      })

      if (type === 'half') {
        // Half day: keep in working but halve hours
        const workingEntry = day.working.find((w) => w.timetasticId === employed.timetasticId)
        if (workingEntry) {
          // Halve existing hours
          const momentDay = this.$moment.unix(day.date.seconds)
          const staffMember = this.$store.state.staff.find((s) => s.timetasticId === employed.timetasticId)
          if (staffMember) {
            const schedule = this.$store.getters.staffHoursByMoment(momentDay, staffMember)
            if (schedule) {
              const fullHours = schedule[momentDay.isoWeekday() - 1]
              workingEntry.hours = Math.round((fullHours / 2) * 10) / 10
            }
          }
        } else {
          // From "Not Working" list — add to working with half hours
          const momentDay = this.$moment.unix(day.date.seconds)
          const staffMember = this.$store.state.staff.find((s) => s.timetasticId === employed.timetasticId)
          if (staffMember) {
            const schedule = this.$store.getters.staffHoursByMoment(momentDay, staffMember)
            if (schedule) {
              const fullHours = schedule[momentDay.isoWeekday() - 1]
              if (fullHours > 0) {
                day.working.push({
                  timetasticId: employed.timetasticId,
                  name: employed.name,
                  hours: Math.round((fullHours / 2) * 10) / 10
                })
              }
            }
          }
        }
      } else {
        // Full day: remove from working
        const idx = day.working.findIndex((w) => w.timetasticId === employed.timetasticId)
        if (idx !== -1) day.working.splice(idx, 1)
      }

      this.cancelHolidaySelection()
      this.$emit('resourcesChanged', day.date.seconds)
    },
    notWorking(day) {
      const workingIds = day.working.map((w) => w.timetasticId)
      return this.availableEmployed.filter((s) => !workingIds.includes(s.timetasticId))
    },
    addSick(day, worker) {
      day.sick.push({
        timetasticId: worker.timetasticId,
        name: worker.name,
        hours: worker.hours
      })
      const idx = day.working.findIndex((w) => w.timetasticId === worker.timetasticId)
      if (idx !== -1) day.working.splice(idx, 1)
      this.$emit('resourcesChanged', day.date.seconds)
    },
    removeSick(day, worker) {
      const idx = day.sick.findIndex((w) => w.timetasticId === worker.timetasticId)
      if (idx !== -1) day.sick.splice(idx, 1)

      // Restore to working if they have scheduled hours for this day
      const alreadyWorking = day.working.some((w) => w.timetasticId === worker.timetasticId)
      if (!alreadyWorking) {
        const momentDay = this.$moment.unix(day.date.seconds)
        const staffMember = this.$store.state.staff.find((s) => s.timetasticId === worker.timetasticId)
        if (staffMember) {
          const schedule = this.$store.getters.staffHoursByMoment(momentDay, staffMember)
          if (schedule) {
            const hours = schedule[momentDay.isoWeekday() - 1]
            if (hours > 0) {
              day.working.push({ timetasticId: worker.timetasticId, name: worker.name, hours })
            }
          }
        }
      }

      this.$emit('resourcesChanged', day.date.seconds)
    },
    removeHoliday(day, employed) {
      // Find by timetasticId first, fallback to id
      let idx = day.holidays.findIndex((w) => w.timetasticId && w.timetasticId === employed.timetasticId)
      if (idx === -1) idx = day.holidays.findIndex((w) => w.id === employed.id)
      if (idx !== -1) day.holidays.splice(idx, 1)

      // Restore working hours
      const staffMember = this.$store.state.staff.find(
        (s) => s.timetasticId === employed.timetasticId || s.name === employed.name
      )
      if (staffMember) {
        const momentDay = this.$moment.unix(day.date.seconds)
        const schedule = this.$store.getters.staffHoursByMoment(momentDay, staffMember)
        if (schedule) {
          const fullHours = schedule[momentDay.isoWeekday() - 1]
          const existingWorking = day.working.find((w) => w.timetasticId === staffMember.timetasticId)

          if (existingWorking) {
            // Was half-day: restore to full hours
            existingWorking.hours = fullHours
          } else if (fullHours > 0) {
            // Was full-day: add back to working
            day.working.push({ timetasticId: staffMember.timetasticId, name: staffMember.name, hours: fullHours })
          }
        }
      }

      this.$emit('resourcesChanged', day.date.seconds)
    },
    selectStaffForHoliday(day, staff) {
      this.holidaySelectionDay = day
      this.holidaySelectionStaff = staff
    },
    cancelHolidaySelection() {
      this.holidaySelectionStaff = null
      this.holidaySelectionDay = null
    },
    getHolidayHours(dateKey, worker) {
      // Called from the template, so it runs at render — staff can still be null there.
      const staffMember = (this.$store.state.staff || []).find((s) => s.timetasticId === worker.timetasticId)
      if (!staffMember) return ''
      const momentDay = this.$moment(dateKey)
      const schedule = this.$store.getters.staffHoursByMoment(momentDay, staffMember)
      if (!schedule) return ''
      const fullHours = schedule[momentDay.isoWeekday() - 1]
      if (!fullHours || fullHours <= 0) return ''
      return worker.type === 'half' ? Math.round((fullHours / 2) * 10) / 10 : fullHours
    },
    getFreelancerHours(staffId) {
      return this.freelancerHoursMap[staffId] || 1
    },
    setFreelancerHours(staffId, value) {
      let v = Math.round(Number(value))
      v = Math.min(8, Math.max(0, v || 0))
      this.freelancerHoursMap[staffId] = v
    },
    addFreelancer(day, freelancer, hours) {
      const existing = day.freelancers.find((f) => f.id === freelancer.id)
      if (existing) {
        existing.hours = hours
      } else {
        day.freelancers.push({
          id: freelancer.id,
          name: freelancer.name,
          hours: hours
        })
      }
      this.$emit('resourcesChanged', day.date.seconds)
    },
    removeFreelancer(day, freelancer) {
      day.freelancers = day.freelancers.filter((f) => {
        return f.id !== freelancer.id
      })
      this.$emit('resourcesChanged', day.date.seconds)
    },
    getOvertimeHours(staffId) {
      return this.overtimeHoursMap[staffId] || 1
    },
    setOvertimeHours(staffId, value) {
      let v = Math.round(Number(value))
      v = Math.min(8, Math.max(0, v || 0))
      this.overtimeHoursMap[staffId] = v
    },
    addOvertime(day, employee, hours) {
      const existing = day.overtime.find((o) => o.timetasticId === employee.timetasticId)
      if (existing) {
        existing.hours = hours
      } else {
        day.overtime.push({
          timetasticId: employee.timetasticId,
          name: employee.name,
          hours: hours
        })
      }
      this.$emit('resourcesChanged', day.date.seconds)
    },
    removeOvertime(day, employee) {
      day.overtime = day.overtime.filter((f) => {
        return f.timetasticId !== employee.timetasticId
      })
      this.$emit('resourcesChanged', day.date.seconds)
    },
    getWorkshopMuRequirement(date) {
      let workshopMuTotal = 0

      for (const task of this.tasksByDay[date]) {
        switch (task.type) {
          case 'finish':
          case 'assemble':
            workshopMuTotal += task?.muFinish || 0
            break
          case 'pack':
            workshopMuTotal += task?.muPack || 0
            break
          case 'dispatch':
            workshopMuTotal += task?.muDispatch || 0
            break
          default:
            break
        }
      }

      return Number(workshopMuTotal.toFixed(1))
    },
    workshopMuCapacityForDay(day) {
      // finish and pack Mu capacity comes from all workshop members
      if (day) {
        let hours = 0
        let workers = day?.working.concat(day?.freelancers).concat(day?.overtime)
        for (const worker of workers) {
          hours += worker.hours
        }
        hours -= day?.cutting // remove cutting hours (only)
        return hours * (this.$store.state.mu.workshopCapacity / 8) // 8 hour day
      }
    },
    getWorkshopMuCapacityPercentage(date) {
      const capacity = this.workshopMuCapacityForDay(this.capacityObject[date])
      const mu = this.getWorkshopMuRequirement(date)
      const workshopMuPercentage = capacity && mu ? Math.round((mu / capacity) * 100) : 0

      return workshopMuPercentage
    },
    muTotalOfDay(date) {
      let muTotal = 0
      for (const task of this.tasksByDay[date]) {
        switch (task.type) {
          case 'cut':
            muTotal += task?.muCut || 0
            break
          case 'finish':
          case 'assemble':
            muTotal += task?.muFinish || 0
            break
          case 'pack':
            muTotal += task?.muPack || 0
            break
          case 'dispatch':
            muTotal += task?.muDispatch || 0
            break
          default:
            break
        }
      }
      return Number(muTotal.toFixed(1))
    }
  }
}
</script>

<style lang="scss" scoped>
#week-resources {
  .header {
    border-bottom: solid 1px $ruleGrey;
  }

  position: absolute;
  overflow-y: scroll;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: width 0.5s;
  .col {
    border-right: solid 1px $ruleGrey;
    &:last-child,
    &.no-border {
      border: none;
    }
    .task-type {
      background: $sand;
      border-top: solid 1px $ruleGrey;
      display: flex;
      justify-content: flex-end;
      padding: 4px;
    }
    .count {
      white-space: nowrap;
    }
  }

  .worker {
    margin: 4px;
    padding: 4px;
    border-radius: 3px;
    cursor: pointer;
    background: #eee;
    display: flex;
    justify-content: space-between;
  }

  .worker-type {
    &.working {
      background: $primary;
      color: white;
    }
    &.holidays {
      background: $accent;
    }
    &.sick {
      background: $accent;
    }
    &.overtime {
      background: $info;
      color: white;
    }
  }

  .worker.holiday .name {
    display: flex;
    align-items: center;
  }

  .add-resource-btn {
    min-width: 25px;
    padding: 5px;
  }

  .worker.preview-add {
    background: rgba($success, 0.25);
    border: 2px dashed $success;
    cursor: default;
  }
  .worker.preview-remove {
    background: rgba($error, 0.25);
    border: 2px dashed $error;
  }

  .draggable {
    min-height: 100%;
    &.disabled {
      opacity: 20%;
    }
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }
  .cutting {
    color: $cut;
  }
  .packing {
    color: $pack;
  }
}
</style>

<style lang="scss">
.hours-input-wrapper {
  display: inline-flex;
  align-items: center;
  border: 2px solid #999;
  border-radius: 4px;
  padding: 2px 6px;
  background: white;
  .hours-input {
    width: 40px;
    text-align: center;
    border: none;
    outline: none;
    background: transparent;
  }
  .hours-suffix {
    color: rgba(0, 0, 0, 0.6);
    margin-left: 2px;
  }
}
</style>
